function getCaseFileTimestamp(record) {
  const rawValue = record?.updatedAtUtc || record?.dateUploadedUtc || record?.createdAtUtc || '';
  const parsedValue = Date.parse(rawValue);
  return Number.isNaN(parsedValue) ? 0 : parsedValue;
}

export function createCaseFileCollectionHelpers({
  normalizeDisplayName,
  getFirstRecordValue,
  getRecordFileType,
  getRecordMediaId,
  getRecordNameCandidates,
  getRecordObjectKey,
  getRecordOriginalFileName,
  getRecordLogicalName,
  getRecordSizeInBytes,
  getRecordTranscriptFileId,
  getRecordScanStatus,
  getRecordLifecycleStatus,
  isTranscriptCaseRecord,
  isMediaCaseRecord,
  isVirusDetectedRecord,
  isVirusStatus,
  isSuccessfulStatus,
  isFailedStatus,
  isSuccessfulLifecycleStatus,
  isScanningLifecycleStatus,
  getEffectiveTranscriptRecord
}) {
  function buildCaseFileKey(record) {
    const recordFileType = getRecordFileType(record);
    let fallbackFileType = '';

    if (!recordFileType) {
      if (isTranscriptCaseRecord(record)) {
        fallbackFileType = 'Transcript';
      } else if (isMediaCaseRecord(record)) {
        fallbackFileType = 'Media';
      }
    }

    return [
      normalizeDisplayName(getFirstRecordValue(record, ['applicationNumber', 'ApplicationNumber'])),
      normalizeDisplayName(getFirstRecordValue(record, ['caseId', 'CaseId'])),
      normalizeDisplayName(getRecordMediaId(record)),
      normalizeDisplayName(recordFileType || fallbackFileType)
    ].join('|');
  }

  function getCaseFileLogicalKeys(record) {
    const applicationNumber = normalizeDisplayName(record?.applicationNumber);
    const caseId = normalizeDisplayName(record?.caseId);
    const fileType = normalizeDisplayName(getRecordFileType(record));
    const objectKey = getRecordObjectKey(record);
    const fileSize = String(getRecordSizeInBytes(record) || '').trim();
    const keys = [];

    if (objectKey) {
      keys.push([applicationNumber, caseId, fileType, 'object', objectKey].join('|'));
    }

    getRecordNameCandidates(record).forEach((name) => {
      keys.push(
        [applicationNumber, caseId, fileType, 'name', name, fileSize].join('|'),
        [applicationNumber, caseId, fileType, 'name', name].join('|')
      );
    });

    const exactKey = buildCaseFileKey(record);
    if (exactKey) {
      keys.push(exactKey);
    }

    return Array.from(new Set(keys)).filter(Boolean);
  }

  function getCaseFileLogicalKey(record) {
    return getCaseFileLogicalKeys(record)[0] || buildCaseFileKey(record);
  }

  function getScanPriority(record) {
    const scanStatus = getRecordScanStatus(record);
    const lifecycleStatus = getRecordLifecycleStatus(record);

    if (isVirusStatus(scanStatus)) {
      return 60;
    }

    if (isSuccessfulStatus(scanStatus)) {
      return 50;
    }

    if (isFailedStatus(scanStatus)) {
      return 40;
    }

    if (isSuccessfulLifecycleStatus(lifecycleStatus)) {
      return 45;
    }

    if (isScanningLifecycleStatus(scanStatus) || isScanningLifecycleStatus(lifecycleStatus)) {
      return 30;
    }

    return 10;
  }

  function getRecordCompletenessScore(record) {
    return [
      getRecordMediaId(record),
      getRecordTranscriptFileId(record),
      getFirstRecordValue(record, ['applicationNumber', 'ApplicationNumber']),
      getFirstRecordValue(record, ['caseId', 'CaseId']),
      getFirstRecordValue(record, ['storedFileName', 'StoredFileName']),
      getFirstRecordValue(record, ['originalFileName', 'OriginalFileName']),
      getRecordScanStatus(record),
      getRecordLifecycleStatus(record),
      record?.updatedAtUtc,
      record?.dateUploadedUtc,
      record?.createdAtUtc
    ].reduce((score, value) => score + (value !== undefined && value !== null && String(value).trim() !== '' ? 1 : 0), 0);
  }

  function chooseBetterCaseFileRecord(existing, incoming) {
    if (!existing) {
      return incoming;
    }

    if (!incoming) {
      return existing;
    }

    const existingPriority = getScanPriority(existing);
    const incomingPriority = getScanPriority(incoming);
    if (incomingPriority !== existingPriority) {
      return incomingPriority > existingPriority ? incoming : existing;
    }

    const incomingTime = getCaseFileTimestamp(incoming);
    const existingTime = getCaseFileTimestamp(existing);
    if (incomingTime !== existingTime) {
      return incomingTime > existingTime ? incoming : existing;
    }

    const incomingScore = getRecordCompletenessScore(incoming);
    const existingScore = getRecordCompletenessScore(existing);
    return incomingScore >= existingScore ? incoming : existing;
  }

  function dedupeCaseFileRecordsExact(records) {
    const exactRecords = new Map();
    (records ?? []).forEach((record) => {
      const key = buildCaseFileKey(record);
      if (!key) {
        return;
      }

      const existing = exactRecords.get(key);
      exactRecords.set(key, existing ? chooseBetterCaseFileRecord(existing, { ...existing, ...record }) : record);
    });

    return Array.from(exactRecords.values());
  }

  function dedupeCaseFileRecords(records) {
    const exactRecords = dedupeCaseFileRecordsExact(records);
    const logicalRecords = new Map();
    exactRecords.forEach((record) => {
      const keys = getCaseFileLogicalKeys(record);
      const existing = keys.map((key) => logicalRecords.get(key)).find(Boolean);
      const chosen = chooseBetterCaseFileRecord(existing, record);

      keys.forEach((key) => {
        logicalRecords.set(key, chosen);
      });
    });

    return Array.from(new Set(logicalRecords.values()));
  }

  function compareCaseFileItems(left, right) {
    const rightTime = getCaseFileTimestamp(right?.mediaRecord);
    const leftTime = getCaseFileTimestamp(left?.mediaRecord);

    if (rightTime !== leftTime) {
      return rightTime - leftTime;
    }

    return getRecordLogicalName(left?.mediaRecord)
      .localeCompare(getRecordLogicalName(right?.mediaRecord));
  }

  function buildCaseFileItems(files) {
    const mediaRecords = [];
    const transcriptByMediaId = new Map();
    const transcriptByName = new Map();
    const exactFiles = dedupeCaseFileRecordsExact(files ?? []);

    exactFiles.forEach((record) => {
      if (isMediaCaseRecord(record)) {
        mediaRecords.push(record);
        return;
      }

      if (!isTranscriptCaseRecord(record)) {
        return;
      }

      const mediaKey = normalizeDisplayName(getRecordMediaId(record));
      const nameKey = getRecordLogicalName(record);

      if (mediaKey) {
        const existing = transcriptByMediaId.get(mediaKey);
        transcriptByMediaId.set(mediaKey, chooseBetterCaseFileRecord(existing, record));
      }

      if (nameKey) {
        const existing = transcriptByName.get(nameKey);
        transcriptByName.set(nameKey, chooseBetterCaseFileRecord(existing, record));
      }
    });

    return dedupeCaseFileRecordsExact(mediaRecords)
      .map((mediaRecord) => {
        const mediaKey = normalizeDisplayName(getRecordMediaId(mediaRecord));
        const mediaNameKey = getRecordLogicalName(mediaRecord);
        const pairedTranscriptRecord = (mediaKey ? transcriptByMediaId.get(mediaKey) : null)
          ?? (mediaNameKey ? transcriptByName.get(mediaNameKey.replace(/^media-/, 'transcript-')) : null)
          ?? null;
        const transcriptRecord = getEffectiveTranscriptRecord(mediaRecord, pairedTranscriptRecord);

        return {
          mediaRecord,
          transcriptRecord
        };
      })
      .sort(compareCaseFileItems);
  }

  return {
    getCaseFileLogicalKeys,
    getCaseFileLogicalKey,
    getScanPriority,
    getRecordCompletenessScore,
    chooseBetterCaseFileRecord,
    dedupeCaseFileRecordsExact,
    dedupeCaseFileRecords,
    getCaseFileTimestamp,
    compareCaseFileItems,
    buildCaseFileItems,
    buildCaseFileKey
  };
}
